import React, { useState, useEffect, useContext } from "react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { Box, Button, Typography, Pagination } from "@mui/material";
import { getPricelist } from "../../graphql/queries";
import { AppContext } from "../../App";
import NoCodePartRow from "./NoCodePartRow";
import { useTheme } from '@mui/material/styles';

const manufacturers = [
  ['Mercedes-Benz', 'BMW', 'Audi', 'Porsche'],
  ['Volkswagen', 'Volvo', 'Jaguar', 'Land Rover']
];

const ROWS_PER_PAGE = 10;

const NoCodeSearch = () => {
  const { userRecord } = useContext(AppContext);
  const [csvData, setCsvData] = useState(null);
  const [filteredParts, setFilteredParts] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [displayedParts, setDisplayedParts] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    const fetchPricelist = async () => {
      if (!userRecord?.activePricelistID) return;

      try {
        setLoading(true);
        const response = await API.graphql(
          graphqlOperation(getPricelist, { id: userRecord.activePricelistID })
        );

        const latestFile = response.data.getPricelist;
        const fileUrl = await Storage.get(latestFile.s3Key, { download: true });

        if (fileUrl.Body) {
          const blob = new Blob([fileUrl.Body], { type: "application/json" });
          const jsonText = await blob.text();
          const jsonData = JSON.parse(jsonText);
          setCsvData(jsonData);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching pricelist:", error);
        setLoading(false);
      }
    };

    fetchPricelist();
  }, [userRecord]);

  useEffect(() => {
    if (!csvData || !selectedManufacturer) {
      setFilteredParts([]);
      return;
    }

    let filtered = [...csvData];

    filtered = filtered.filter(row => {
      if (!row || !row["PART NUMBER"]) return false;
      return row["PART NUMBER"].toString().toLowerCase().includes(selectedManufacturer.toLowerCase());
    });

    setFilteredParts(filtered);
  }, [csvData, selectedManufacturer]);

  // Separate effect for pagination
  useEffect(() => {
    if (!filteredParts.length) return;

    const startIndex = (page - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    const paginatedParts = filteredParts.slice(startIndex, endIndex);
    
    setDisplayedParts(paginatedParts);
  }, [filteredParts, page]);

  const handleManufacturerClick = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setPage(1); // Reset to first page when changing filter
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlePartClick = (part) => {
    console.log('Part clicked:', part);
  };

  // Calculate total pages based on filtered results
  const totalPages = Math.ceil(filteredParts.length / ROWS_PER_PAGE);

  return (
    <Box sx={{ padding: 2, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Navigation Tabs */}
      

      {/* First Row of Manufacturer Buttons */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2,
        marginBottom: 2
      }}>
        {manufacturers[0].map((manufacturer) => (
          <Button
            key={manufacturer}
            onClick={() => handleManufacturerClick(manufacturer)}
            sx={{
              color: selectedManufacturer === manufacturer ? theme.palette.background.default : theme.palette.primary.main,
              backgroundColor: selectedManufacturer === manufacturer ? theme.palette.primary.main : theme.palette.background.default,
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: '25px',
              flex: 1,
              padding: '10px',
              '&:hover': {
                backgroundColor: selectedManufacturer === manufacturer ? theme.palette.primary.main : theme.palette.background.paper,
                opacity: 0.8
              }
            }}
          >
            {manufacturer}
          </Button>
        ))}
      </Box>

      {/* Second Row of Manufacturer Buttons */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2,
        marginBottom: 3
      }}>
        {manufacturers[1].map((manufacturer) => (
          <Button
            key={manufacturer}
            onClick={() => handleManufacturerClick(manufacturer)}
            sx={{
              color: selectedManufacturer === manufacturer ? theme.palette.background.default : theme.palette.primary.main,
              backgroundColor: selectedManufacturer === manufacturer ? theme.palette.primary.main : theme.palette.background.default,
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: '25px',
              flex: 1,
              padding: '10px',
              '&:hover': {
                backgroundColor: selectedManufacturer === manufacturer ? theme.palette.primary.main : theme.palette.background.paper,
                opacity: 0.8
              }
            }}
          >
            {manufacturer}
          </Button>
        ))}
      </Box>

      {/* Green Line Separator */}
      <Box sx={{ 
        height: '2px', 
        backgroundColor: theme.palette.primary.main, 
        marginBottom: 3 
      }} />

      {/* Results */}
      <Box sx={{ marginTop: 3 }}>
        {loading ? (
          <Typography sx={{ color: theme.palette.primary.main }}>Loading...</Typography>
        ) : (
          <>
            {selectedManufacturer && displayedParts.map((row, i) => {
              console.log(row);
              const part = {
                partNumber: row["PART NUMBER"],
                price: row["PRICE"],
                image1: row["IMAGE1"],
                image2: row["IMAGE2"],
                image3: row["IMAGE3"],
              };
              return (
                <NoCodePartRow
                  key={i}
                  part={part}
                />
              );
            })}

            {/* Pagination Controls */}
            {selectedManufacturer && filteredParts.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginTop: 3,
                '& .MuiPagination-ul .MuiPaginationItem-root': {
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.default,
                    '&:hover': {
                      backgroundColor: '#35d33d',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(64, 243, 72, 0.1)',
                  },
                },
              }}>
                <Pagination 
                  count={totalPages}
                  page={page} 
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default NoCodeSearch;
